/**
 * O que o painel DERIVA de cada Caso, e o que ele afirma sobre si.
 *
 * Fora dos componentes de propósito: é regra, não pintura — mesma razão de
 * `shared/dominio/sla.ts` não morar dentro do `SeloDeSla`. Aqui o teste
 * exercita o comportamento sem renderizar nada, e o componente só escolhe
 * como mostrar.
 */

import { classificarSla, descreverPrazo } from '../../shared/dominio/sla';
import { formatarDataHora, formatarRelativo, type Instante } from '../../shared/dominio/datas';
import { type EtapaResolvida, etapaDoEstado, relogioDoEstado } from './etapas';
import type { CasoDaFila, FrenteLida, ResumoDasFrentes, SituacaoDaResposta } from './api/tipos';

/** Marca ao lado do nome quando não há vínculo ativo com o IW (`S9`). */
export const ROTULO_DO_PACIENTE_PROVISORIO = 'Do e-mail, sem vínculo no IW';

/**
 * O que o cartão diz sobre o relógio, já em palavras.
 *
 * Quatro formas, porque são quatro as situações que a `BR-006` separa, e
 * nenhuma delas é campo vazio: `prazo` quando há um relógio nosso correndo,
 * `decorrido` no vão em que a operadora é quem deve (`S4`), `ausente` quando
 * não há prazo e a tela precisa dizer POR QUE (`E2`, `E3`), e `nenhum`
 * quando o Caso parou de correr de vez.
 */
export interface LeituraDoRelogio {
  readonly rotulo: string;
  readonly prazo: Instante | null;
  readonly texto: string | null;
  /**
   * Só a contagem (`faltam 2h 0min`, `estourado há 1h`), sem a data: para quem já mostra a data ao lado. `null` sem
   * prazo, ou quando navegador e servidor discordam sobre vencer (aí `texto` cai para a data).
   */
  readonly contagem: string | null;
  /**
   * A situação do prazo como o SERVIDOR a calculou (`situacaoDaResposta`/`situacaoDaImplantacao`); `null` quando não
   * há prazo nosso correndo. O texto de contagem abaixo é só exibição: quem diz se venceu é o servidor.
   */
  readonly situacao: SituacaoDaResposta | null;
  readonly estourado: boolean;
}

/**
 * O texto de contagem do prazo. Quem diz se venceu é o SERVIDOR (`situacao`): ele desconta as esperas por terceiro, e o
 * relógio do navegador não. Se os dois discordam sobre vencer, a tela mostra só a data, sem afirmar nem "faltam" nem
 * "estourado".
 */
function contagemDoPrazo(prazo: Instante, situacao: SituacaoDaResposta, agora: Instante): string | null {
  const classificacao = classificarSla(prazo, agora);
  const vencidoAqui = classificacao.minutosRestantes !== null && classificacao.minutosRestantes < 0;
  return vencidoAqui === (situacao === 'Overdue') ? descreverPrazo(classificacao) : null;
}

function relogioComPrazo(
  rotulo: string,
  prazo: Instante,
  situacao: SituacaoDaResposta,
  agora: Instante,
): LeituraDoRelogio {
  const contagem = contagemDoPrazo(prazo, situacao, agora);
  return {
    rotulo,
    prazo,
    texto: contagem ?? `prazo ${formatarDataHora(prazo)}`,
    contagem,
    situacao,
    estourado: situacao === 'Overdue',
  };
}

/** O desfecho já medido da resposta (`Respondido no prazo`/`fora do prazo`), para as linhas em que nenhum prazo corre. */
function situacaoRespondida(caso: CasoDaFila): SituacaoDaResposta | null {
  return caso.situacaoDaResposta === 'AnsweredOnTime' || caso.situacaoDaResposta === 'AnsweredLate'
    ? caso.situacaoDaResposta
    : null;
}

export function lerRelogio(caso: CasoDaFila, agora: Instante = new Date()): LeituraDoRelogio {
  switch (relogioDoEstado(caso.estado)) {
    case 'resposta': {
      if (caso.prazoDeRespostaEm === null) {
        // E3: o Caso veio sem prazo herdado. Não há atraso a destacar porque
        // não há prazo a estourar — dizer "no prazo" aqui seria folga falsa.
        return {
          rotulo: 'Resposta',
          prazo: null,
          texto: 'Sem prazo de resposta',
          contagem: null,
          situacao: null,
          estourado: false,
        };
      }
      return relogioComPrazo('Resposta', caso.prazoDeRespostaEm, caso.situacaoDaResposta, agora);
    }
    case 'decorrido': {
      // S4: o relógio de resposta PAROU na entrega e o de implantação só
      // nasce na autorização. Entre os dois a espera é da operadora —
      // mostrar tempo decorrido diz a verdade; deixar um prazo correndo
      // marcaria como atraso da Anery o que não é.
      const desde = caso.entrouNoEstadoEm;
      return {
        rotulo: 'Com a operadora',
        prazo: null,
        texto: desde === null ? 'sem prazo nosso' : `${formatarRelativo(desde, agora)} · sem prazo nosso`,
        contagem: null,
        situacao: situacaoRespondida(caso),
        estourado: false,
      };
    }
    case 'implantacao': {
      if (caso.prazoDeImplantacaoEm === null) {
        // E2. A razão exata (sem modalidade × sem horas configuradas) não
        // vem no item do painel; a frase cobre as duas sem inventar qual é,
        // e o detalhe do Caso tem a razão por extenso.
        return {
          rotulo: 'Implantação',
          prazo: null,
          texto: 'Sem prazo: modalidade ou horas não definidas',
          contagem: null,
          situacao: null,
          estourado: false,
        };
      }
      return relogioComPrazo('Implantação', caso.prazoDeImplantacaoEm, caso.situacaoDaImplantacao, agora);
    }
    case 'nenhum':
      return {
        rotulo: 'Sem relógio',
        prazo: null,
        texto: 'nenhum prazo corre',
        contagem: null,
        situacao: situacaoRespondida(caso),
        estourado: false,
      };
  }
}

/** Últimos 8 caracteres do UUID: o bastante para distinguir linhas quando o Caso não tem nada legível além do ID. */
export const idCurto = (id: string): string => id.slice(-8);

/**
 * Como a lista chama o Caso no lugar do UUID: operadora e modalidade (`BRADESCO · ID com ventilação`). O Caso não tem
 * código próprio, então sem nenhuma das duas cai para `Caso` + ID curto. O UUID inteiro fica no `title` e no `href`.
 */
export function identificacaoDoCaso(caso: CasoDaFila): string {
  const modalidade =
    caso.modalidade === null ? null : caso.modalidade === 'ID' && caso.ventilacaoContinua ? 'ID com ventilação' : caso.modalidade;
  const partes = [caso.operadora, modalidade].filter((parte): parte is string => parte !== null && parte !== '');
  return partes.length === 0 ? `Caso ${idCurto(caso.id)}` : partes.join(' · ');
}

/** O que a tela afirma sobre a própria fonte. Some se a tela deixar de ler o banco. */
export const ORIGEM_DO_QUADRO =
  'Os Casos em captação, agrupados por etapa. Abaixo, o que chegou por e-mail e ainda não virou Caso. Aqui você acompanha; para agir, abra o Caso.';

/**
 * E5: a permanência é regra, e fica VISÍVEL na tela. Sem isto o Caso some da
 * etapa operadora numa atualização qualquer e quem olhava conclui, com toda
 * razão, que ele evaporou.
 */
export const REGRA_DA_PERMANENCIA =
  'Casos autorizados e recusados ficam na etapa Operadora por 7 dias a partir da entrada no estado, e depois saem do painel. Casos encerrados não aparecem.';

export function agruparPorEtapa(
  casos: readonly CasoDaFila[],
): ReadonlyMap<EtapaResolvida, readonly CasoDaFila[]> {
  const grupos = new Map<EtapaResolvida, CasoDaFila[]>();

  for (const caso of casos) {
    const etapa = etapaDoEstado(caso.estado);
    // `null` é o estado que o painel não mostra de propósito (`encerrado`). O
    // servidor já o recortou; esta guarda existe para o caso de ele passar.
    if (etapa === null) continue;
    const atual = grupos.get(etapa);
    if (atual === undefined) grupos.set(etapa, [caso]);
    else atual.push(caso);
  }

  return grupos;
}

/** Um núcleo oferecido no filtro. O valor é o id; o rótulo é o que se lê. */
export interface NucleoParaFiltro {
  readonly id: string;
  readonly nome: string;
}

export interface OpcoesDeFiltro {
  readonly nucleos: readonly NucleoParaFiltro[];
  readonly operadoras: readonly string[];
}

export const SEM_OPCOES: OpcoesDeFiltro = { nucleos: [], operadoras: [] };

/**
 * O que o filtro oferece, tirado do que a leitura TROUXE.
 *
 * É o `E6` resolvido por construção, e não por verificação: a sessão de
 * núcleo nunca recebe Caso de outro núcleo, então a opção do outro núcleo
 * não existe para ser escolhida. A `BR-006` diz exatamente isso — "a tela só
 * oferece o que ela pode ver" —, e é mais seguro que oferecer tudo e torcer
 * para o servidor recusar.
 *
 * As opções se ACUMULAM entre leituras (`conhecidas`). Sem isso, escolher um
 * núcleo esvaziaria a própria lista de núcleos na leitura seguinte, que já
 * vem filtrada — a pessoa ficaria presa no filtro que acabou de aplicar. O
 * acúmulo pode manter um núcleo cujo último Caso saiu do painel; filtrar por
 * ele devolve vazio, que é a verdade, e o escopo nunca aumenta.
 */
export function opcoesDeFiltro(
  casos: readonly CasoDaFila[],
  conhecidas: OpcoesDeFiltro = SEM_OPCOES,
): OpcoesDeFiltro {
  const nucleos = new Map(conhecidas.nucleos.map((nucleo) => [nucleo.id, nucleo]));
  const operadoras = new Set(conhecidas.operadoras);

  for (const caso of casos) {
    if (caso.nucleoId !== null) {
      nucleos.set(caso.nucleoId, { id: caso.nucleoId, nome: caso.nucleoNome ?? caso.nucleoId });
    }
    operadoras.add(caso.operadora ?? 'Sem operadora');
  }

  const emOrdem = (a: string, b: string) => a.localeCompare(b, 'pt-BR');

  return {
    nucleos: [...nucleos.values()].sort((a, b) => emOrdem(a.nome, b.nome)),
    operadoras: [...operadoras].sort(emOrdem),
  };
}

// ---------------------------------------------------------------------------
// A trilha de CADASTRO — a única das cinco frentes com dado real hoje.
//
// As outras quatro (avaliação, documentação hospitalar, validação do PAD,
// equipe) não têm coluna, tabela nem evento no banco: só existiam na fixture
// do protótipo. Desenhar uma barra vazia para elas afirmaria "zero passos
// feitos", e a verdade é "não medimos" — que é diferente, e pior de mentir
// num quadro que a coordenação usa para cobrar.
//
// Por isso o cartão mostra UMA trilha. As outras voltam quando existirem no
// domínio, sem reescrever nenhum componente: `Trilhas` já aceita as cinco.
// ---------------------------------------------------------------------------

import { ROTULOS_DO_ITEM_DA_PENDENCIA, type ItemDaPendencia, type ResumoDaCompletude } from './api/tipos';
import type { EstadoDaTrilha, TravaDoCaso, Trilha } from '../../shared/ui/tiposDaEsteira';

/**
 * Os cinco itens do cadastro (`app.pendencia_captacao`, CHECK da coluna
 * `item`). O denominador da barra é este, e não o número de linhas gravadas:
 * a varredura só grava o que FALTA, então item sem linha é item completo.
 */
export const TOTAL_DO_CADASTRO = 5;

/** `nome` é o mínimo para abrir o Caso; faltar ele é mais grave que o resto. */
const ITENS_DE_NIVEL_UM: readonly ItemDaPendencia[] = ['Name'];

export const TRILHA_DE_CADASTRO: Trilha = 'cadastro';

/**
 * `null` = não há linha de pendência nenhuma, e **não dá para afirmar nada**.
 *
 * Era um defeito: com zero pendências abertas a barra saía cheia e verde, e o
 * cartão dizia "cadastro completo" para um Caso que ninguém conferiu — os 32
 * Casos do seed caíam exatamente aí.
 *
 * E o conserto não pode ser "diga que não foi varrido", porque o estado é
 * AMBÍGUO no domínio: a varredura só grava o que FALTA, então zero linhas é
 * "nunca varrido" OU "varrido e estava completo desde o início", e as duas são
 * indistinguíveis nesta tabela. A saída honesta é não desenhar a barra e
 * dizer o fato — não há pendência registrada —, sem concluir dele.
 *
 * O que desfaz a ambiguidade é a varredura carimbar quando rodou.
 * `app.caso.inventario_visto_em` existe para isso e nunca é preenchido; está
 * registrado como pendência conhecida.
 */
export function trilhaDoCadastro(caso: {
  readonly pendenciasAbertas: number;
  readonly itensPendentes: readonly ItemDaPendencia[];
  readonly totalDePendencias: number;
  readonly completude?: ResumoDaCompletude | null;
}): EstadoDaTrilha | null {
  // Com a completude verificada (TP-058) a trilha vem da MESMA conta do painel "O que falta para completar": ela roda
  // sempre, entao nao ha o "nunca varrido" das pendencias, e o cartao nao discorda do detalhe.
  if (caso.completude != null) {
    const { cadastroTotal, cadastroPendente, nivelUmCompleto } = caso.completude;
    return {
      feitos: Math.max(cadastroTotal - cadastroPendente.length, 0),
      total: cadastroTotal,
      situacao: cadastroPendente.length === 0 ? '' : `falta ${cadastroPendente.map((r) => r.toLowerCase()).join(', ')}`,
      tom: cadastroPendente.length === 0 ? 'ok' : nivelUmCompleto ? 'warn' : 'block',
      unidade: 'item',
    };
  }

  if (caso.totalDePendencias === 0) return null;

  const feitos = Math.max(TOTAL_DO_CADASTRO - caso.pendenciasAbertas, 0);
  const faltaOMinimo = caso.itensPendentes.some((item) => ITENS_DE_NIVEL_UM.includes(item));

  return {
    feitos,
    total: TOTAL_DO_CADASTRO,
    // A situação NOMEIA o que falta. "3 pendências" manda a pessoa abrir o
    // Caso para descobrir o quê; "falta CPF, endereço" já diz o próximo passo.
    situacao:
      caso.itensPendentes.length === 0
        ? ''
        : `falta ${caso.itensPendentes.map((item) => ROTULOS_DO_ITEM_DA_PENDENCIA[item].toLowerCase()).join(', ')}`,
    // `block` quando falta o mínimo para abrir; `warn` enquanto falta o
    // completo; `ok` quando fechou. Mesma escada de três tons do protótipo.
    tom: caso.pendenciasAbertas === 0 ? 'ok' : faltaOMinimo ? 'block' : 'warn',
    unidade: 'item',
  };
}

/**
 * As cinco frentes, como a `Trilhas` as desenha (`TP-044` `T10`).
 *
 * `null` quando a operadora não tem curadoria — e aí a tela **diz isso**, em
 * vez de desenhar trilha vazia. É a mesma disciplina de `trilhaDoCadastro`:
 * barra vazia afirma "zero passos feitos", e "não medimos" é outra coisa.
 *
 * Nenhuma primitiva nova. `Trilhas`, `PontoDaTrilha`, `BarraDaTrilha` e
 * `TravaDoCaso` já sabiam fazer tudo isto desde 25/09; o que faltava era o
 * modelo atrás.
 */
export function trilhasDoCaso(frentes: ResumoDasFrentes | null, agora: Date = new Date()): {
  readonly trilhas: Partial<Record<Trilha, EstadoDaTrilha>>;
  readonly ordem: Trilha[];
  readonly rotulos: Partial<Record<Trilha, string>>;
  readonly rotulosLongos: Partial<Record<Trilha, string>>;
  readonly trava: TravaDoCaso | undefined;
} | null {
  if (frentes === null || !frentes.curado || frentes.frentes.length === 0) return null;

  const trilhas: Partial<Record<Trilha, EstadoDaTrilha>> = {};
  const rotulos: Partial<Record<Trilha, string>> = {};
  const rotulosLongos: Partial<Record<Trilha, string>> = {};
  const ordem: Trilha[] = [];

  for (const f of frentes.frentes) {
    ordem.push(f.frente);
    rotulos[f.frente] = f.rotulo;
    rotulosLongos[f.frente] = f.rotulo;
    trilhas[f.frente] = estadoDaFrente(f, agora);
  }

  const travando = frentes.frentes.find((f) => f.travando);

  return {
    trilhas,
    ordem,
    rotulos,
    rotulosLongos,
    trava:
      travando === undefined
        ? undefined
        : {
            trilha: travando.frente,
            motivo: travando.esperando === null ? 'ninguém pegou' : (travando.porQueEspera ?? ''),
            responsavel: '',
          },
  };
}

function estadoDaFrente(f: FrenteLida, agora: Date): EstadoDaTrilha {
  if (f.estado === 'dispensada') {
    // Dispensada é VERDE e DITA: ela conta como satisfeita para as portas, e
    // o motivo fica na frase. Omiti-la apagaria a auditoria onde ela é cobrada.
    return {
      feitos: f.totalDePassos,
      total: f.totalDePassos,
      situacao: `dispensada: ${f.motivoDaDispensa ?? 'sem motivo registrado'}`,
      tom: 'ok',
    };
  }

  if (f.estado === 'concluida') {
    return { feitos: f.totalDePassos, total: f.totalDePassos, situacao: '', tom: 'ok' };
  }

  const parada = idadeDesde(f.paradaDesde, agora);

  // Esperar outra frente NÃO é trava e não é culpa de ninguém: tom `info`, e a
  // frase diz o quê e por quê. Apontar isso como atraso mandaria a cobrança
  // para quem não pode andar.
  if (f.esperando !== null) {
    return {
      feitos: f.passosFeitos,
      total: f.totalDePassos,
      situacao: f.porQueEspera ?? `espera ${f.esperando}`,
      idade: parada,
      tom: 'info',
      esperando: true,
    };
  }

  const estourou = f.prazoEm !== null && f.prazoEm.getTime() <= agora.getTime();

  return {
    feitos: f.passosFeitos,
    total: f.totalDePassos,
    situacao: f.travando ? 'ninguém pegou' : '',
    idade: parada,
    tom: estourou ? 'block' : f.travando ? 'warn' : 'neutral',
  };
}

/** "há 5h", "há 2d". Vazio quando acabou de andar — ruído não ajuda a cobrar. */
function idadeDesde(desde: Instante, agora: Date): string | undefined {
  const minutos = Math.floor((agora.getTime() - desde.getTime()) / 60000);
  if (minutos < 60) return undefined;
  const horas = Math.floor(minutos / 60);
  return horas < 48 ? `há ${horas}h` : `há ${Math.floor(horas / 24)}d`;
}
